import { useState } from 'react';
import { Link } from 'react-router-dom';

import type { CoffeeViewNavState } from '@/analytics/events';
import { CoffeePhoto } from '@/components/common/CoffeePhoto';
import { useImageFallback } from '@/hooks/useImageFallback';
import { useShelfToggle } from '@/hooks/useShelfToggle';
import { formatRelativeTime } from '@/lib/formatters';
import type { ShelfCoffee } from '@/types/feed';

interface ShelfCardProps {
  // Only the fields the ShelfCoffee fragment actually selects — the shelf
  // query does not fetch the full Coffee.
  coffee: ShelfCoffee;
  addedAt: string;
  // Re-syncs the shelf lists after a successful removal.
  refetch: () => Promise<void>;
}

export function ShelfCard({ coffee, addedAt, refetch }: ShelfCardProps) {
  const coffeeImageUrl = coffee.imageUrl ?? null;
  const { hasError, onError } = useImageFallback(coffeeImageUrl);
  const showImage = coffeeImageUrl !== null && !hasError;
  const roasterName = coffee.roaster?.name ?? 'Unknown roaster';
  // This card only renders for entries currently on the shelf, so the
  // toggle always removes.
  const { isToggling, error, toggle } = useShelfToggle(true, coffee.id, 'account');
  // isToggling drops as soon as the mutation resolves, BEFORE the refetch
  // settles — and by then the hook's isOnShelf has already flipped, so a
  // click in that window would dispatch the opposite mutation and silently
  // undo the remove. Bracket the whole handler (mutation + refetch), like
  // the old page-level removingId guard did.
  const [isPending, setIsPending] = useState(false);
  const isBusy = isToggling || isPending;

  const handleRemove = async () => {
    setIsPending(true);
    try {
      if (await toggle()) await refetch();
    } finally {
      setIsPending(false);
    }
  };

  return (
    <>
      <div
        className="bg-wolisho-card rounded-xl p-4 flex items-center gap-4"
        data-testid="shelf-card"
      >
        <Link
          to={`/coffee/${coffee.id}`}
          state={{ source: 'shelf' } satisfies CoffeeViewNavState}
          className="flex items-center gap-4 flex-1 min-w-0"
        >
          {/* No image container when the coffee has no photo. */}
          {showImage && (
            <CoffeePhoto
              src={coffeeImageUrl}
              alt={coffee.name}
              sizeClassName="w-16 h-20"
              testId="shelf-card-photo"
              onError={onError}
            />
          )}
          {/* Divider between the framed tile and the copy, matching the coffee
              detail layout. Only with a photo — otherwise it's a stray edge. */}
          <div
            className={`flex-1 min-w-0 ${showImage ? 'border-l border-wolisho-brown/10 pl-4' : ''}`}
          >
            <p className="text-black font-bold truncate">{coffee.name}</p>
            <p className="text-black text-sm">
              by <span className="text-wolisho-red">{roasterName}</span>
            </p>
            <p className="text-black text-sm opacity-75">{coffee.origin}</p>
            <p className="text-xs text-black/40 mt-1" data-testid="shelf-card-added">
              Added {formatRelativeTime(addedAt)}
            </p>
          </div>
        </Link>
        <button
          type="button"
          data-testid="shelf-remove-button"
          disabled={isBusy}
          onClick={() => void handleRemove()}
          className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-wolisho-brown hover:bg-wolisho-brown hover:text-white transition-colors"
          aria-label={`Remove ${coffee.name} from shelf`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
            <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
          </svg>
        </button>
      </div>
      {error && (
        <p
          role="alert"
          className="text-wolisho-red text-sm text-center"
          data-testid="shelf-card-error"
        >
          Failed to remove coffee from shelf. Please try again.
        </p>
      )}
    </>
  );
}
